/**
 * MUI MeasurementInput Component
 * 
 * This component is the Material UI equivalent of the original MeasurementInput component.
 * It provides a measurement input field with unit display.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import ReducersIndex from 'reduxModules/ducks';
import { InputLabel } from '../atoms';
import { UnitInput } from '../atoms';
import { getCurrentValue } from 'reduxModules/selectors/containerSelectors';
import { round, toNumber } from 'lodash';
import { MeasurementInputProps } from 'types/components/molecules';
import { AnyAction } from '@reduxjs/toolkit';

/**
 * MUI MeasurementInput component
 * 
 * @param props - Component props
 * @returns The MUI MeasurementInput component
 */
const MuiMeasurementInput: React.FC<MeasurementInputProps> = (props) => {
  // Extract props, providing defaults
  const { 
    step = 1, 
    min: minProp, 
    max: maxProp,
    unit, 
    units, 
    className = '',
    dispatcher,
    name,
    label,
    ...otherProps 
  } = props;
  
  // Handle both range object and direct min/max props
  const range = props.range || {};
  const min = minProp ?? range.min ?? -Infinity;
  const max = maxProp ?? range.max ?? +Infinity;
  
  // Use either unit or units prop (backward compatibility)
  const unitToDisplay = unit || units || '';
  
  // Helper for formatting value with unit
  const toString = useCallback((value: number | string): string => 
    `${value ? `${round(Number(value))} ${unitToDisplay}` : ""}`, 
    [unitToDisplay]
  );

  // Helper function to safely call getCurrentValue with TypeScript
  const safeGetValue = (state: any, param: string) => {
    // Cast the function to allow any number of arguments
    const getValue = getCurrentValue as any;
    return getValue(state, param);
  };

  // Get current value from Redux state if dispatcher is provided
  const currentValue = useSelector((state: any) => 
    dispatcher ? safeGetValue(state, dispatcher) : null
  ) as number | null;
  
  const [value, setValue] = useState<number>(props.value || 0);
  const [valueString, setValueString] = useState<string>("");
  const dispatch = useDispatch();

  /**
   * Update the value within the min/max range
   * 
   * @param newValue - The new value
   */
  const updateValue = (newValue: number | string): void => {
    const numValue = Number(newValue);
    setValue(Math.max(min as number, Math.min(numValue, max as number)));
    
    // Call onChange handler if provided
    if (props.onChange) {
      props.onChange(numValue);
    }
  };

  // Update local state when prop value or Redux value changes
  useEffect(() => {
    if (props.value !== undefined) {
      setValue(props.value);
    } else if (typeof currentValue === 'number') {
      setValue(currentValue);
    }
  }, [props.value, currentValue]);

  // Update display string and dispatch to Redux when value changes
  useEffect(() => {
    setValueString(toString(value));
    
    if (dispatcher) {
      dispatch(ReducersIndex.container[dispatcher](value) as unknown as AnyAction);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toString, value, dispatcher]);

  /**
   * Handle key down event
   * 
   * @param evt - The key down event
   */
  const handleKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>): void => {
    const key = evt.key;
    if (!['Delete', 'Backspace', 'Tab'].includes(key) && !/^[0-9.]+$/.test(key)) {
      evt.preventDefault();
    }
    
    const currentValue = (evt.target as HTMLInputElement).value;
    
    switch (key) {
      case 'Enter':
      case 'Tab':
        updateValue(toNumber(currentValue));
        break;
      case 'ArrowUp':
        updateValue(round(value + (step as number)));
        break;
      case 'ArrowDown':
        updateValue(round(value - (step as number)));
        break;
      default:
        break;
    }
  };

  /**
   * Handle blur event
   * 
   * @param evt - The blur event
   */
  const handleBlur = (evt: React.FocusEvent<HTMLInputElement>): void => {
    updateValue(parseFloat(evt.target.value));
    
    // Call onBlur handler if provided
    if (props.onBlur) {
      props.onBlur(evt);
    }
  };

  // Combine all props for the UnitInput component
  const enhancedProps = {
    ...otherProps,
    name,
    spinStep: step,
    value,
    setValue: updateValue,
    valueString,
    setValueString,
    handleKeyDown,
    handleBlur,
    unit: unitToDisplay,
    disabled: props.disabled
  };

  return (
    <Box className={`d-flex flex-wrap gap-1 ${className}`}>
      {label && <InputLabel>{label}</InputLabel>}
      <UnitInput {...enhancedProps} />
    </Box>
  );
};

export type { MeasurementInputProps };
export default MuiMeasurementInput;
